import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HotTable, HotTableRef } from "@handsontable/react-wrapper";
import Handsontable from "handsontable";
import { registerAllModules } from "handsontable/registry";
import "handsontable/styles/handsontable.min.css";
import "handsontable/styles/ht-theme-main.min.css";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import ModalConfirm from "../../components/ui/modal/ModalConfirm";
import { InitDataContableContext } from "../../context/InitDataContable";
import { showMsg } from "../../utils/Constants.tsx";
import { useScrollbarGutter } from "../../components/reportes/reportesUtils.ts";
import {
  COL,
  COL_HEADERS,
  CatalogosBudget,
  MESES_CORTOS,
  NUM_COLS,
  ResultadoValidacion,
  crearCatalogo,
  esColCatalogo,
  esColMonto,
  filaVacia,
  formatMonto,
  normalizarCatalogo,
  parseMonto,
  validarPresupuesto,
} from "./budgetValidation.ts";
import "./loadBudget.css";

registerAllModules();

const FILAS_INICIALES = 30;
const MAX_PROBLEMAS_LISTA = 150;

const filasVacias = (n: number) =>
  Array.from({ length: n }, () => Array(NUM_COLS).fill(null));

// Months abbreviated in the header (full names in tooltips / messages)
const HEADERS_GRILLA = COL_HEADERS.map((h, c) =>
  c >= COL.MES1 ? MESES_CORTOS[c - COL.MES1] : h,
);

// Stable references: the wrapper re-applies every prop that changes
const CONTEXT_MENU: Handsontable.GridSettings["contextMenu"] = [
  "row_above",
  "row_below",
  "---------",
  "remove_row",
  "---------",
  "undo",
  "redo",
  "---------",
  "copy",
  "cut",
];
const FILL_HANDLE = { direction: "vertical", autoInsertRow: true } as const;

const clp = (v: number) => `$ ${formatMonto(Math.round(v))}`;

// Amount cells: es-CL thousands, right aligned
function montoRenderer(
  this: unknown,
  instance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties,
) {
  const mostrado =
    typeof value === "number" && Number.isFinite(value)
      ? formatMonto(value)
      : value;
  Handsontable.renderers.TextRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    mostrado,
    cellProperties,
  );
}

export default function LoadBudget() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  // Read the instance when needed: the wrapper creates it after mount
  const hotRef = useRef<HotTableRef>(null);
  const getHot = () => hotRef.current?.hotInstance ?? null;
  useScrollbarGutter();

  const {
    centrosCosto,
    cuentasContables,
    itemsContables,
    naturalezasContables,
  } = useContext(InitDataContableContext);

  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [, setNeedFetchingDoc] = useState(false);
  const [goHome, setGoHome] = useState(false);
  const [resultado, setResultado] = useState<ResultadoValidacion | null>(null);
  const [verAvisos, setVerAvisos] = useState(true);
  const [alturaGrilla] = useState(() =>
    Math.max(380, Math.min(640, window.innerHeight - 360)),
  );

  useEffect(() => {
    if (goHome) navigate("/", { replace: true });
  }, [goHome]);

  /* ---------------- Catalogs ("code-name" labels) ---------------- */

  const catalogos: CatalogosBudget = useMemo(
    () => ({
      centros: crearCatalogo(
        centrosCosto,
        (e: any) => e.id,
        (e: any) => e.nombre,
      ),
      cuentas: crearCatalogo(
        cuentasContables,
        (e: any) => e.codigo_cuenta,
        (e: any) => e.nombre,
      ),
      items: crearCatalogo(
        itemsContables,
        (e: any) => e.codigo_cuenta_item,
        (e: any) => e.nombre,
      ),
      naturalezas: crearCatalogo(
        naturalezasContables,
        (e: any) => e.codigo_item_naturaleza,
        (e: any) => e.nombre,
      ),
    }),
    [centrosCosto, cuentasContables, itemsContables, naturalezasContables],
  );
  const catalogosListos =
    catalogos.centros.etiquetas.length > 0 &&
    catalogos.cuentas.etiquetas.length > 0 &&
    catalogos.items.etiquetas.length > 0 &&
    catalogos.naturalezas.etiquetas.length > 0;

  const catalogoDeCol = useCallback(
    (c: number) =>
      [
        catalogos.centros,
        catalogos.cuentas,
        catalogos.items,
        catalogos.naturalezas,
      ][c],
    [catalogos],
  );

  /* ---------------- Validation ---------------- */

  // Read by the cell renderer / cells callback without re-rendering React
  const resultadoRef = useRef<ResultadoValidacion | null>(null);
  const mensajesRef = useRef<Map<string, string>>(new Map());

  const revalidar = useCallback(() => {
    const h = getHot();
    if (!h || h.isDestroyed) return null;
    const r = validarPresupuesto(h.getSourceData() as unknown[][], catalogos);
    const mensajes = new Map<string, string>();
    r.problemas.forEach((p) => {
      const k = `${p.fila}:${p.col}`;
      mensajes.set(k, [mensajes.get(k), p.mensaje].filter(Boolean).join("\n"));
    });
    resultadoRef.current = r;
    mensajesRef.current = mensajes;
    setResultado(r);
    h.render();
    return r;
  }, [catalogos]);

  // Catalogs arrive after the grid: validate again with them
  useEffect(() => {
    if (catalogosListos) revalidar();
  }, [catalogosListos, revalidar]);

  /* ---------------- Grid settings ---------------- */

  const columns = useMemo<Handsontable.ColumnSettings[]>(
    () =>
      COL_HEADERS.map((_, c) => {
        if (esColCatalogo(c)) {
          return {
            editor: "autocomplete",
            source: catalogoDeCol(c).etiquetas,
            filter: true,
            strict: false,
            visibleRows: 10,
            trimDropdown: false,
            width: c === COL.CENTRO ? 210 : 230,
          };
        }
        if (c === COL.ANIO) return { width: 70 };
        if (c === COL.OBS) return { width: 220 };
        return { renderer: montoRenderer, width: c === COL.TOTAL ? 130 : 115 };
      }),
    [catalogoDeCol],
  );

  const cells = useCallback(
    (row: number, col: number): Handsontable.CellMeta => {
      const sev = resultadoRef.current?.celdas.get(`${row}:${col}`);
      const base = esColMonto(col)
        ? "htRight"
        : col === COL.ANIO
          ? "htCenter"
          : "";
      return {
        className: [
          base,
          col === COL.TOTAL ? "lb-col-total" : "",
          sev === "error"
            ? "lb-cell-error"
            : sev === "aviso"
              ? "lb-cell-aviso"
              : "",
        ]
          .filter(Boolean)
          .join(" "),
      };
    },
    [],
  );

  // Clean typed / pasted values before they reach the data
  const beforeChange = useCallback(
    (changes: (Handsontable.CellChange | null)[]) => {
      changes.forEach((ch) => {
        if (!ch) return;
        const col = Number(ch[1]);
        const anterior = ch[2];
        const nuevo = ch[3];
        if (esColCatalogo(col)) {
          ch[3] = normalizarCatalogo(nuevo, catalogoDeCol(col));
        } else if (esColMonto(col)) {
          // Re-opening a cell shows its raw number: keep it as is
          if (typeof anterior === "number" && String(anterior) === nuevo) {
            ch[3] = anterior;
            return;
          }
          const m = parseMonto(nuevo);
          ch[3] = m === null ? null : Number.isNaN(m) ? nuevo : m;
        } else if (col === COL.ANIO) {
          const t = String(nuevo ?? "").trim();
          ch[3] = /^\d{4}$/.test(t) ? Number(t) : t || null;
        } else if (col === COL.OBS && typeof nuevo === "string") {
          ch[3] = nuevo.trim() || null;
        }
      });
    },
    [catalogoDeCol],
  );

  // Tooltip with the problems of the cell
  const afterRenderer = useCallback(
    (td: HTMLTableCellElement, row: number, col: number) => {
      const r = getHot()?.toPhysicalRow(row) ?? row;
      const msg = mensajesRef.current.get(`${r}:${col}`);
      if (msg) td.title = msg;
      else td.removeAttribute("title");
    },
    [],
  );

  // Only real edits validate again (new empty rows change nothing)
  const afterChange = useCallback(
    (_changes: unknown, source: string) => {
      if (source !== "loadData" && source !== "updateData") revalidar();
    },
    [revalidar],
  );
  const afterRemoveRow = useCallback(() => revalidar(), [revalidar]);

  /* ---------------- Actions ---------------- */

  const irACelda = (fila: number, col: number) => {
    const h = getHot();
    if (!h) return;
    const visual = h.toVisualRow(fila);
    h.selectCell(visual, col);
    h.scrollViewportTo({ row: visual, col });
    document
      .querySelector(".lb-grid-card")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const aplicarCambios = (cambios: [number, number, unknown][]) => {
    const h = getHot();
    if (!h || !cambios.length) return 0;
    h.setDataAtCell(
      cambios.map(([fila, col, v]) => [h.toVisualRow(fila), col, v]),
      "lb-accion",
    );
    return cambios.length;
  };

  const completarMesesConCero = () => {
    const datos = (getHot()?.getSourceData() ?? []) as unknown[][];
    const cambios: [number, number, unknown][] = [];
    datos.forEach((fila, r) => {
      if (filaVacia(fila)) return;
      for (let c = COL.MES1; c <= COL.MES12; c++) {
        if (parseMonto(fila[c]) === null) cambios.push([r, c, 0]);
      }
    });
    const n = aplicarCambios(cambios);
    showMsg(
      toast,
      n ? "success" : "info",
      "Meses vacíos",
      n ? `${n} celdas completadas con 0.` : "No hay meses vacíos.",
    );
  };

  const calcularTotalesAnuales = () => {
    const datos = (getHot()?.getSourceData() ?? []) as unknown[][];
    const cambios: [number, number, unknown][] = [];
    datos.forEach((fila, r) => {
      if (filaVacia(fila)) return;
      const meses = fila.slice(COL.MES1, COL.MES12 + 1).map(parseMonto);
      if (meses.some((m) => m === null || Number.isNaN(m))) return;
      const suma = meses.reduce<number>((a, m) => a + (m as number), 0);
      const total = parseMonto(fila[COL.TOTAL]);
      if (total === null || Number.isNaN(total) || Math.abs(total - suma) > 1)
        cambios.push([r, COL.TOTAL, Math.round(suma * 10000) / 10000]);
    });
    const n = aplicarCambios(cambios);
    showMsg(
      toast,
      n ? "success" : "info",
      "Ppto. Año",
      n
        ? `${n} totales anuales calculados como la suma de sus 12 meses.`
        : "Todos los totales anuales ya cuadran con sus meses (o faltan meses).",
    );
  };

  const limpiarTabla = () => {
    if (
      !window.confirm(
        "¿Limpiar toda la tabla? Se perderán los datos que no hayas registrado.",
      )
    )
      return;
    getHot()?.loadData(filasVacias(FILAS_INICIALES));
    revalidar();
  };

  const descargarPlantilla = async () => {
    try {
      const xlsx = await import("xlsx");
      const wb = xlsx.utils.book_new();
      const ejemplo = [
        catalogos.centros.etiquetas[0] ?? "",
        catalogos.cuentas.etiquetas[0] ?? "",
        catalogos.items.etiquetas[0] ?? "",
        catalogos.naturalezas.etiquetas[0] ?? "",
        new Date().getFullYear() + 1,
        1200000,
        "Ejemplo: borra esta fila",
        ...Array(12).fill(100000),
      ];
      xlsx.utils.book_append_sheet(
        wb,
        xlsx.utils.aoa_to_sheet([COL_HEADERS, ejemplo]),
        "Presupuesto",
      );
      const hoja = (nombre: string, etiquetas: string[]) =>
        xlsx.utils.book_append_sheet(
          wb,
          xlsx.utils.aoa_to_sheet([
            ["Valor para pegar", "Código"],
            ...etiquetas.map((e) => [e, e.split("-")[0]]),
          ]),
          nombre,
        );
      hoja("Centros de costo", catalogos.centros.etiquetas);
      hoja("Cuentas contables", catalogos.cuentas.etiquetas);
      hoja("Items contables", catalogos.items.etiquetas);
      hoja("Naturalezas", catalogos.naturalezas.etiquetas);
      xlsx.writeFile(wb, "plantilla_presupuesto.xlsx");
    } catch (e) {
      console.error(e);
      showMsg(toast, "error", "Plantilla", "No se pudo generar la plantilla.");
    }
  };

  const registrar = () => {
    const r = revalidar();
    if (!r || r.resumen.filas === 0) {
      showMsg(toast, "warn", "Advertencia", "No hay datos en la tabla.");
      return;
    }
    if (r.errores > 0) {
      showMsg(
        toast,
        "warn",
        "Advertencia",
        `Hay ${r.errores} ${r.errores === 1 ? "dato inválido" : "datos inválidos"}. Corrígelos antes de registrar (lista bajo la tabla).`,
      );
      const p = r.problemas[0];
      if (p) irACelda(p.fila, p.col);
      return;
    }
    setShowModalConfirm(true);
  };

  /* ---------------- View ---------------- */

  const res = resultado?.resumen;
  const errores = resultado?.errores ?? 0;
  const avisos = resultado?.avisos ?? 0;
  const filas = res?.filas ?? 0;
  const listo = catalogosListos && filas > 0 && errores === 0;
  const problemasVisibles = (resultado?.problemas ?? []).filter(
    (p) => verAvisos || p.severidad === "error",
  );

  const textoConfirmacion = res
    ? `¿Registrar ${filas} ${filas === 1 ? "línea" : "líneas"} de presupuesto ${
        res.anios.length ? `(${res.anios.join(", ")}) ` : ""
      }por un total anual de ${clp(res.totalAnual)}?${
        res.descuadres
          ? ` Atención: ${res.descuadres} ${res.descuadres === 1 ? "línea no cuadra" : "líneas no cuadran"} con la suma de sus meses.`
          : ""
      }`
    : "¿Estás seguro de guardar este presupuesto?";

  return (
    <div className="lb-page">
      <Toast ref={toast} />

      {/* ---------------- Header ---------------- */}
      <header className="lb-hero">
        <div className="lb-hero-icono" aria-hidden="true">
          <i className="pi pi-table" />
        </div>
        <div className="lb-hero-texto">
          <span className="lb-eyebrow">Control de gestión · Presupuestos</span>
          <h2 className="lb-titulo">Carga de presupuesto anual</h2>
          <p className="lb-subtitulo">
            Escribe o pega desde Excel. Los montos aceptan formato chileno
            (1.234.567 o 1.234,50) y en las columnas de catálogo basta con el
            código: se completa con su nombre.
          </p>
        </div>
        <button
          type="button"
          className="lb-btn lb-btn-hero"
          onClick={descargarPlantilla}
          disabled={!catalogosListos}
          title="Incluye los catálogos de centros, cuentas, items y naturalezas"
        >
          <i className="pi pi-download" aria-hidden="true" /> Plantilla Excel
        </button>
      </header>

      <ol className="lb-pasos" aria-label="Pasos">
        <li>
          <span>1</span> Descarga la plantilla o usa tu Excel
        </li>
        <li>
          <span>2</span> Pega las filas en la tabla
        </li>
        <li>
          <span>3</span> Corrige lo marcado en rojo
        </li>
        <li>
          <span>4</span> Registra el presupuesto
        </li>
      </ol>

      {/* ---------------- Summary ---------------- */}
      <section className="lb-kpis" aria-label="Resumen de la carga">
        <div className="lb-kpi">
          <span className="lb-kpi-label">Líneas</span>
          <strong>{formatMonto(filas)}</strong>
          <span className="lb-kpi-sub">
            {res?.centros ?? 0} centros de costo
          </span>
        </div>
        <div className="lb-kpi">
          <span className="lb-kpi-label">Año(s)</span>
          <strong>{res?.anios.length ? res.anios.join(", ") : "—"}</strong>
          <span className="lb-kpi-sub">según la columna Año</span>
        </div>
        <div className="lb-kpi">
          <span className="lb-kpi-label">Total anual</span>
          <strong>{clp(res?.totalAnual ?? 0)}</strong>
          <span className="lb-kpi-sub">columna Ppto. Año</span>
        </div>
        <div
          className={`lb-kpi${
            res && Math.abs(res.totalAnual - res.totalMeses) > 1
              ? " lb-kpi-aviso"
              : ""
          }`}
        >
          <span className="lb-kpi-label">Suma de meses</span>
          <strong>{clp(res?.totalMeses ?? 0)}</strong>
          <span className="lb-kpi-sub">
            {res && Math.abs(res.totalAnual - res.totalMeses) > 1
              ? `Diferencia ${clp(res.totalMeses - res.totalAnual)}`
              : "cuadra con el total anual"}
          </span>
        </div>
        <div
          className={`lb-kpi lb-kpi-estado ${
            !catalogosListos
              ? "is-cargando"
              : errores
                ? "is-error"
                : filas
                  ? "is-ok"
                  : ""
          }`}
        >
          <span className="lb-kpi-label">Estado</span>
          <strong>
            <i
              className={`pi ${
                !catalogosListos
                  ? "pi-spin pi-spinner"
                  : errores
                    ? "pi-times-circle"
                    : filas
                      ? "pi-check-circle"
                      : "pi-inbox"
              }`}
              aria-hidden="true"
            />{" "}
            {!catalogosListos
              ? "Cargando catálogos"
              : errores
                ? `${errores} ${errores === 1 ? "error" : "errores"}`
                : filas
                  ? "Listo para registrar"
                  : "Sin datos"}
          </strong>
          <span className="lb-kpi-sub">
            {avisos
              ? `${avisos} ${avisos === 1 ? "aviso" : "avisos"} (no bloquean)`
              : "sin avisos"}
          </span>
        </div>
      </section>

      {/* ---------------- Grid ---------------- */}
      <section className="lb-card lb-grid-card">
        <div className="lb-toolbar">
          <div className="lb-toolbar-grupo">
            <button
              type="button"
              className="lb-btn lb-btn-outline"
              onClick={calcularTotalesAnuales}
              title="Ppto. Año = suma de los 12 meses, en las filas vacías o que no cuadran"
            >
              <i className="pi pi-calculator" aria-hidden="true" /> Calcular
              Ppto. Año
            </button>
            <button
              type="button"
              className="lb-btn lb-btn-outline"
              onClick={completarMesesConCero}
              title="Completa con 0 los meses vacíos de las filas con datos"
            >
              <i className="pi pi-pencil" aria-hidden="true" /> Meses vacíos = 0
            </button>
          </div>
          <div className="lb-toolbar-grupo">
            <span className="lb-leyenda">
              <span className="lb-dot lb-dot-error" /> Error
              <span className="lb-dot lb-dot-aviso" /> Aviso
            </span>
            <button
              type="button"
              className="lb-btn lb-btn-danger"
              onClick={limpiarTabla}
            >
              <i className="pi pi-eraser" aria-hidden="true" /> Limpiar
            </button>
          </div>
        </div>

        <div className="lb-grid">
          <HotTable
            id="hotTable"
            ref={hotRef}
            themeName="ht-theme-main"
            startRows={FILAS_INICIALES}
            startCols={NUM_COLS}
            colHeaders={HEADERS_GRILLA}
            rowHeaders={true}
            columns={columns}
            cells={cells}
            maxCols={NUM_COLS}
            minSpareRows={1}
            fixedColumnsStart={1}
            width="100%"
            height={alturaGrilla}
            rowHeights={30}
            wordWrap={false}
            stretchH="none"
            navigableHeaders={true}
            tabNavigation={true}
            autoWrapRow={true}
            autoWrapCol={true}
            contextMenu={CONTEXT_MENU}
            undo={true}
            fillHandle={FILL_HANDLE}
            headerClassName="htLeft"
            licenseKey="non-commercial-and-evaluation"
            beforeChange={beforeChange}
            afterChange={afterChange}
            afterRemoveRow={afterRemoveRow}
            afterRenderer={afterRenderer}
          />
        </div>
        <p className="lb-ayuda">
          <i className="pi pi-info-circle" aria-hidden="true" /> Clic derecho
          para insertar o eliminar filas · Ctrl+Z deshace · arrastra la esquina
          de una celda para copiarla hacia abajo · los meses se muestran
          abreviados (Ene = Ppto. Enero).
        </p>
      </section>

      {/* ---------------- Problems ---------------- */}
      {resultado && resultado.problemas.length > 0 ? (
        <section className="lb-card lb-problemas" aria-live="polite">
          <div className="lb-problemas-head">
            <h3>
              <i className="pi pi-list" aria-hidden="true" /> Revisión de la
              carga
              <span className="lb-chip lb-chip-error">{errores} errores</span>
              <span className="lb-chip lb-chip-aviso">{avisos} avisos</span>
            </h3>
            {avisos ? (
              <label className="lb-switch">
                <input
                  type="checkbox"
                  checked={verAvisos}
                  onChange={(e) => setVerAvisos(e.target.checked)}
                />
                Mostrar avisos
              </label>
            ) : null}
          </div>
          <ul className="lb-problemas-lista">
            {problemasVisibles.slice(0, MAX_PROBLEMAS_LISTA).map((p, i) => (
              <li key={`${p.fila}-${p.col}-${i}`}>
                <button
                  type="button"
                  className={`lb-problema lb-problema-${p.severidad}`}
                  onClick={() => irACelda(p.fila, p.col)}
                  title="Ir a la celda"
                >
                  <span className="lb-problema-pos">
                    Fila {p.fila + 1} · {COL_HEADERS[p.col]}
                  </span>
                  <span className="lb-problema-msg">{p.mensaje}</span>
                  <i className="pi pi-arrow-right" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {problemasVisibles.length > MAX_PROBLEMAS_LISTA ? (
            <p className="lb-ayuda">
              Se muestran los primeros {MAX_PROBLEMAS_LISTA} de{" "}
              {problemasVisibles.length}.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* ---------------- Action bar ---------------- */}
      <div className="lb-footer">
        <div className="lb-footer-resumen">
          <span>
            <strong>{formatMonto(filas)}</strong>{" "}
            {filas === 1 ? "línea" : "líneas"}
          </span>
          <span>
            Total anual <strong>{clp(res?.totalAnual ?? 0)}</strong>
          </span>
          {errores ? (
            <span className="lb-footer-error">
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />{" "}
              Corrige {errores} {errores === 1 ? "error" : "errores"} para
              registrar
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="lb-btn lb-btn-primary"
          id="btn_registrar_presupuesto"
          onClick={registrar}
          disabled={!listo}
        >
          <i className="pi pi-cloud-upload" aria-hidden="true" /> REGISTRAR
        </button>
      </div>

      {showModalConfirm ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ModalConfirm
            header="Confirmación"
            text={textoConfirmacion}
            show={true}
            setShowModalConfirm={setShowModalConfirm}
            setDocId={0}
            setNeedFetchingDoc={setNeedFetchingDoc}
            data={getHot()}
            jsonDestination="budget"
            data2=""
            setGoHome={setGoHome}
          />
        </div>
      ) : null}
    </div>
  );
}
